/**
 * The row people actually use.
 *
 * Nine times in ten, opening ReadCommons means going back to the one book that
 * was open yesterday. So this row sits above everything, its covers are larger
 * than the grid's, and one click lands in the reader at the saved position: the
 * reader restores from the stored progress on its own, so the plain book link is
 * already a resume link and no extra parameter is needed.
 *
 * It scrolls horizontally rather than wrapping. A second line here would push the
 * shelf below the fold on a laptop, and the shelf is the other half of the screen.
 */

import { Cover, ProgressBar, remainingLabel } from "./BookCard";
import { href } from "../lib/route";
import { t, useUiLang } from "../i18n";
import type { BookSummary } from "../lib/types";

function ContinueCard({ book }: { book: BookSummary }) {
  useUiLang();
  const percent = book.progress?.percent ?? 0;
  const remaining = remainingLabel(book);
  return (
    <a
      href={href({ name: "book", id: book.id })}
      aria-label={t("continueResume", { title: book.title }) + (remaining ? `, ${remaining}` : "")}
      className="group flex w-fib144 shrink-0 snap-start flex-col gap-3 rounded-lg outline-offset-4 sm:w-fib233"
    >
      <div className="relative transition-transform duration-300 [transition-timing-function:var(--ease-out)] group-hover:-translate-y-1">
        <Cover book={book} />
        <span
          className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-accent)] text-[var(--color-on-accent)] opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 coarse:opacity-100"
          aria-hidden="true"
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor">
            <path d="M6.5 4.5v11l9-5.5-9-5.5z" />
          </svg>
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        <h3 className="line-clamp-2 text-sm font-medium leading-snug text-[var(--color-ink)] transition-colors duration-150 group-hover:text-[var(--color-accent)]">
          {book.title}
        </h3>
        <ProgressBar percent={percent} />
        <p className="text-2xs text-[var(--color-muted)]">
          {remaining ?? t("bookPercentRead", { n: Math.round(percent * 100) })}
        </p>
      </div>
    </a>
  );
}

function ContinueSkeleton() {
  return (
    <div className="flex w-fib144 shrink-0 animate-pulse flex-col gap-3 sm:w-fib233" aria-hidden="true">
      <div className="aspect-[2/3] w-full rounded-md bg-[var(--color-surface)]" />
      <div className="h-3 w-4/5 rounded bg-[var(--color-surface)]" />
      <div className="h-fib3 w-full rounded bg-[var(--color-surface)]" />
    </div>
  );
}

export function ContinueRow({ books, loading }: { books: BookSummary[]; loading: boolean }) {
  useUiLang();
  // Nothing started yet: the row would be an empty promise, so it is not drawn.
  if (!loading && books.length === 0) return null;

  return (
    <section aria-labelledby="continue-heading" className="mb-8 sm:mb-12">
      <h2
        id="continue-heading"
        className="mb-4 text-2xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted)]"
      >
        {t("continueTitle")}
      </h2>
      <div className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-2 sm:gap-5">
        {loading
          ? [0, 1, 2, 3, 4].map((slot) => <ContinueSkeleton key={slot} />)
          : books.map((book) => <ContinueCard key={book.id} book={book} />)}
      </div>
    </section>
  );
}
